import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import client from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { PERM, hasGlobalPermission } from "@/lib/permissions";
import { SelectStep } from "@/pages/bulkUpdate/SelectStep";
import { ReviewStep } from "@/pages/bulkUpdate/ReviewStep";
import { ProgressStep } from "@/pages/bulkUpdate/ProgressStep";
import { ResultsStep } from "@/pages/bulkUpdate/ResultsStep";
import { buildReviewRows, parseDealerCodes, runBulkUpdate, COOKIE_CONSENT_BULK_ENABLED } from "@/pages/bulkUpdate/actions";

/** Bulk Update Dealers — see docs/specs/bulk-update-dealers-20260904.md.
 * A single route drives all four steps via local state rather than
 * sub-routes, since the flow is entirely client-side (dealer codes +
 * review selection) and a refresh naturally restarting at step 1 is
 * simpler than reconstructing router state. */
export default function BulkUpdateDealers() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Same gate as CookieConsentsCard's "Queue Setup"/"Create OneTrust Record"
  // and Settings -> OneTrust Cookie Consent, so this action is only ever
  // offered to someone who could also trigger it from those other places.
  const canManageCookieConsents = hasGlobalPermission(user, PERM.COOKIE_CONSENTS_MANAGE);

  const [step, setStep] = useState("select"); // select | review | progress | results
  const [allDealers, setAllDealers] = useState(null);

  const [action, setAction] = useState(null);
  const [params, setParams] = useState({});
  // Pre-filled from dealers checked on the dealers list page (state, not clipboard).
  const [dealerCodesText, setDealerCodesText] = useState(() => (location.state?.dealerCodes || []).join("\n"));

  const [rows, setRows] = useState([]);
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results, setResults] = useState([]);

  useEffect(() => {
    client
      .get(`/dealers`, { params: { all: true } })
      .then((r) => setAllDealers(r.data.items))
      .catch(() => toast.error("Failed to load dealers"));
  }, []);

  const goToReview = () => {
    const dealersByCode = new Map((allDealers || []).map((d) => [d.global_id, d]));
    const codes = parseDealerCodes(dealerCodesText);
    const builtRows = buildReviewRows(codes, dealersByCode, action, params);
    setRows(builtRows);
    setSelectedRowIds(
      new Set(builtRows.filter((r) => !r.notFound && !r.duplicate && !r.na).map((r) => r.rowId)),
    );
    setStep("review");
  };

  const runUpdate = async () => {
    // TEMP DISABLE — OneTrust tenant is LIVE; see COOKIE_CONSENT_BULK_ENABLED
    // in actions.js to re-enable (flip that flag back to true and remove
    // this guard once bulk OneTrust queueing is ready).
    if (action === "cookieConsent" && !COOKIE_CONSENT_BULK_ENABLED) {
      toast.error(t("bulkUpdate.cookieConsentBulkDisabled"));
      return;
    }
    const selectedRows = rows.filter((r) => selectedRowIds.has(r.rowId));
    setProgress({ done: 0, total: selectedRows.length });
    setStep("progress");
    const outcome = await runBulkUpdate(action, params, selectedRows, {
      onProgress: (done, total) => setProgress({ done, total }),
    });
    setResults(outcome);
    setStep("results");
  };

  const startOver = () => {
    setAction(null);
    setParams({});
    setDealerCodesText("");
    setRows([]);
    setSelectedRowIds(new Set());
    setProgress({ done: 0, total: 0 });
    setResults([]);
    setStep("select");
  };

  return (
    <div className="flex h-full flex-col p-6 md:p-10" data-testid="bulk-update-dealers-page">
      <button
        data-testid="bulk-update-back-to-list"
        onClick={() => navigate("/dealers")}
        className="mb-4 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" />
        {t("bulkUpdate.results.backToDealers")}
      </button>

      <div className="mb-6">
        <p className="data-label mb-2">
          {t("dealers.title").toUpperCase()} · {t("bulkUpdate.title").toUpperCase()}
        </p>
        <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{t("bulkUpdate.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("bulkUpdate.subtitle")}</p>
      </div>

      {step === "select" && (
        <SelectStep
          action={action}
          setAction={setAction}
          params={params}
          setParams={setParams}
          dealerCodesText={dealerCodesText}
          setDealerCodesText={setDealerCodesText}
          loadingDealers={allDealers === null}
          onNext={goToReview}
          canManageCookieConsents={canManageCookieConsents}
        />
      )}

      {step === "review" && (
        <ReviewStep
          action={action}
          params={params}
          rows={rows}
          selectedRowIds={selectedRowIds}
          setSelectedRowIds={setSelectedRowIds}
          onBack={() => setStep("select")}
          onUpdate={runUpdate}
        />
      )}

      {step === "progress" && <ProgressStep progress={progress} />}

      {step === "results" && (
        <ResultsStep results={results} onBackToDealers={() => navigate("/dealers")} onStartAnother={startOver} />
      )}
    </div>
  );
}
